import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, Sparkles, Gem, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import NotificationBellButton from '../../components/NotificationBellButton';
import { HostBottomNav } from '../../components/HostBottomNav';
import { useBackTarget } from '../../hooks/useBackTarget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const font = "'Inria Serif', serif";
const MAX_REASON_CHARACTERS = 71;

const getReasonContentLength = (value) => value.split('\n').reduce((total, line) => total + line.replace(/^•\s?/, '').length, 0);

const criteriaCards = [
  {
    id: 'unique',
    title: 'Unique experience',
    body: 'A standout concept or signature experience guests will enjoy',
    Icon: Sparkles,
  },
  {
    id: 'atmosphere',
    title: 'Great atmosphere',
    body: 'A distinctive setting that creates a sense of awe',
    Icon: Gem,
  },
  {
    id: 'worth',
    title: 'Worth visiting',
    body: 'A place that Allures guests and leaves a lasting impression',
    Icon: MapPin,
  },
];

const shellCardStyle = {
  width: '100%',
  background: 'var(--surface)',
  borderRadius: 28,
  border: '1px solid rgba(112, 63, 246, 0.1)',
  boxShadow: 'var(--premium-shadow-main)',
};

const dottedInputStyle = {
  width: '100%',
  border: 'none',
  borderBottom: '2px dotted var(--card-border)',
  outline: 'none',
  background: 'transparent',
  padding: '4px 0 10px',
  fontFamily: font,
  fontSize: 18,
  color: 'var(--text)',
  textAlign: 'center',
};

export default function HostReferVenuePage() {
  const navigate = useNavigate();
  const goBack = useBackTarget();
  const reasonInputRef = useRef(null);
  const [step, setStep] = useState('intro');
  const [venueName, setVenueName] = useState('');
  const [venueType, setVenueType] = useState('');
  const [venueLocation, setVenueLocation] = useState('');
  const [venueLink, setVenueLink] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const handleSubmit = async () => {
    if (!venueName.trim() || !venueType.trim() || !venueLocation.trim() || !reason.trim()) {
      toast.error('Please complete every required field before submitting');
      return;
    }
    if (getReasonContentLength(reason) === 0) {
      toast.error('Please add at least one short-description point');
      return;
    }
    if (!reason.includes('•')) {
      toast.error('Please add at least one bullet point in the short description');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/referrals/venue`,
        {
          venue_name: venueName.trim(),
          venue_address: venueLocation.trim(),
          venue_category: venueType.trim(),
          venue_link: venueLink.trim(),
          reason: reason.trim(),
        },
        { headers },
      );
      toast.success('Venue referral submitted');
      navigate('/host/active-qr', { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to submit venue referral');
    } finally {
      setSubmitting(false);
    }
  };

  const updateReasonAtCursor = (insertion) => {
    const textarea = reasonInputRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart || 0;
    const end = textarea.selectionEnd || 0;
    const nextValue = `${reason.slice(0, start)}${insertion}${reason.slice(end)}`;
    const nextCursor = Math.min(start + insertion.length, nextValue.length);
    if (getReasonContentLength(nextValue) > MAX_REASON_CHARACTERS) {
      return;
    }
    setReason(nextValue);
    window.requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(nextCursor, nextCursor);
    });
  };

  const handleReasonFocus = () => {
    if (!reason) {
      setReason('• ');
      window.requestAnimationFrame(() => {
        reasonInputRef.current?.focus();
        reasonInputRef.current?.setSelectionRange(2, 2);
      });
    }
  };

  const handleReasonKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    updateReasonAtCursor('\n• ');
  };

  const handleReasonBlur = () => {
    if (reason.trim() === '•') {
      setReason('');
    }
  };

  return (
    <div className="app-shell premium-page" data-testid="host-refer-venue-page" style={{ backgroundColor: 'var(--app-bg)' }}>
      <div style={{ padding: '16px 20px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button onClick={() => (step === 'form' ? setStep('intro') : goBack())} data-testid="host-refer-venue-back-button" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
          <ChevronLeft size={30} color="var(--text)" strokeWidth={1.5} />
        </button>
        <NotificationBellButton buttonTestId="host-refer-venue-bell-button" popupTestId="host-refer-venue-notification-popup" />
      </div>

      <div className="page-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 0 }}>
        <img src="/allure-logo.png" alt="Allure" style={{ width: 156, height: 132, objectFit: 'contain', marginTop: 16 }} />

        {step === 'intro' ? (
          <div style={{ width: '100%', display: 'grid', gap: 18, marginTop: 6 }}>
            <div style={{ ...shellCardStyle, padding: '18px 18px 20px', textAlign: 'center' }}>
              <p style={{ fontFamily: font, fontSize: 24, fontWeight: 700, color: 'var(--text)', margin: 0, lineHeight: 1.05 }}>Refer a venue to Allure</p>
              <p style={{ fontFamily: font, fontSize: 18, fontWeight: 700, color: 'var(--text)', margin: '14px 0 0', lineHeight: 1.06 }}>Bigger community, greater rewards.<br />Know a great place your guests<br />would love?</p>
            </div>

            <p style={{ fontFamily: font, fontSize: 22, fontWeight: 700, color: 'var(--text)', textAlign: 'center', margin: 0 }}>What we’re looking for:</p>

            <div style={{ display: 'grid', gap: 16 }}>
              {criteriaCards.map((card) => {
                const CardIcon = card.Icon;
                return (
                  <div key={card.id} data-testid={`host-refer-venue-criteria-${card.id}`} style={{ ...shellCardStyle, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{ width: 56, height: 56, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--status-success-surface)', flexShrink: 0 }}>
                      <CardIcon size={26} color="var(--status-success-text)" strokeWidth={1.75} />
                    </div>
                    <div style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                      <p style={{ fontFamily: font, fontSize: 20, fontWeight: 700, color: 'var(--status-success-text)', margin: 0, lineHeight: 1.06 }}>{card.title}</p>
                      <p style={{ fontFamily: font, fontSize: 15, color: 'var(--text-2)', margin: 0, lineHeight: 1.16 }}>{card.body}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ ...shellCardStyle, boxShadow: 'none', padding: '14px 18px', textAlign: 'center' }}>
              <p style={{ fontFamily: font, fontSize: 18, color: 'var(--accent-text-soft)', margin: 0, lineHeight: 1.14 }}>Note: If it doesn’t meet the above,<br />it won’t be approved.</p>
            </div>

            <button type="button" onClick={() => setStep('form')} data-testid="host-refer-venue-continue-button" className="premium-pill-button" style={{ width: '100%', padding: '14px 0', fontFamily: font, fontSize: 22, cursor: 'pointer' }}>
              Continue
            </button>
          </div>
        ) : (
          <div style={{ width: '100%', ...shellCardStyle, marginTop: 14, padding: '22px 18px 28px', display: 'grid', gap: 24 }}>
            <div style={{ display: 'grid', gap: 18 }}>
              <div>
                <input value={venueName} onChange={(event) => setVenueName(event.target.value)} data-testid="host-refer-venue-name-input" placeholder="Venue name" style={dottedInputStyle} />
              </div>
              <div>
                <input value={venueType} onChange={(event) => setVenueType(event.target.value)} data-testid="host-refer-venue-type-input" placeholder="Type of venue (e.g. Cocktail bar)" style={dottedInputStyle} />
              </div>
              <div>
                <input value={venueLocation} onChange={(event) => setVenueLocation(event.target.value)} data-testid="host-refer-venue-location-input" placeholder="Location (city or area)" style={dottedInputStyle} />
              </div>
              <div>
                <input value={venueLink} onChange={(event) => setVenueLink(event.target.value)} data-testid="host-refer-venue-link-input" placeholder="Instagram / website (optional)" style={dottedInputStyle} />
              </div>
            </div>

            <div style={{ display: 'grid', gap: 14 }}>
              <div style={{ display: 'grid', gap: 6, textAlign: 'center' }}>
                <p style={{ fontFamily: font, fontSize: 22, fontWeight: 700, color: 'var(--text)', margin: 0, lineHeight: 1.06 }}>Short description</p>
                <p style={{ fontFamily: font, fontSize: 15, color: 'var(--text)', margin: 0, lineHeight: 1.2 }}>Minimum one bullet point required</p>
              </div>
              <div style={{ position: 'relative' }}>
                {!reason ? (
                  <div data-testid="host-refer-venue-reason-overlay" style={{ position: 'absolute', top: 14, left: 8, right: 8, pointerEvents: 'none', fontFamily: "'Manrope', sans-serif", fontSize: 13.2, color: 'var(--text-3)', lineHeight: 1.22, letterSpacing: '-0.02em', whiteSpace: 'pre' }}>
                    {'• Incredibly massive burgers\n• Hand-rolled ice cream on a cold plate\n• Stunning views with unique cocktails'}
                  </div>
                ) : null}
                <textarea
                  ref={reasonInputRef}
                  value={reason}
                  onChange={(event) => {
                    if (getReasonContentLength(event.target.value) <= MAX_REASON_CHARACTERS) {
                      setReason(event.target.value);
                    }
                  }}
                  onFocus={handleReasonFocus}
                  onKeyDown={handleReasonKeyDown}
                  onBlur={handleReasonBlur}
                  data-testid="host-refer-venue-reason-input"
                  placeholder=""
                  rows={3}
                  style={{ width: '100%', minHeight: 92, borderRadius: 24, border: '1px solid rgba(112, 63, 246, 0.1)', background: 'var(--surface)', boxShadow: 'inset 0 3px 10px rgba(112, 63, 246, 0.10)', padding: '14px 12px 42px', fontFamily: "'Manrope', sans-serif", fontSize: 14, color: 'var(--text)', outline: 'none', resize: 'none', lineHeight: 1.28, letterSpacing: '-0.01em' }}
                />
                <p data-testid="host-refer-venue-reason-counter" style={{ position: 'absolute', right: 12, bottom: 12, margin: 0, fontFamily: "'Manrope', sans-serif", fontSize: 12, color: getReasonContentLength(reason) >= MAX_REASON_CHARACTERS ? 'var(--accent-text-soft)' : 'var(--text-3)', textAlign: 'right' }}>{getReasonContentLength(reason)}/{MAX_REASON_CHARACTERS}</p>
              </div>
            </div>

            <button type="button" onClick={handleSubmit} disabled={submitting} data-testid="host-refer-venue-submit-button" style={{ width: '100%', padding: '14px 0', borderRadius: 9999, border: 'none', background: 'var(--accent-purple)', color: '#FFFFFF', fontFamily: font, fontSize: 22, fontWeight: 700, cursor: 'pointer', boxShadow: 'var(--premium-shadow-main)', opacity: submitting ? 0.7 : 1 }}>
              {submitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        )}
      </div>

      <HostBottomNav />
    </div>
  );
}